// Client del Home VL: `sales/kpis` y `cancellations` pegan al wizard.
import { apiFetch } from '../../../client';
import { UnexpectedShapeError } from '../../../errors';
import type { Financier } from '../../retail-org/backoffice';
import type {
    CancellationItem,
    Cancellations7d,
    HomeApi,
    SalesKpisResponse,
    SalesMixSlice,
} from './homeContract';

const BASE_URL = import.meta.env.VITE_API_RETAIL_WIZARD_BASE_URL;

/** Respuesta de `GET /backoffice/sales/kpis`; el adaptador la traduce al contrato de la pantalla. */
interface SiloSalesKpis {
    soldTodayCents: number;
    unitsToday: number;
    averageTicketCents: number;
    deltaVsYesterdayPct: number | null;
    baselineSameWeekdayCents: number;
    baselineSameWeekdayUnits?: number;
    mix: { financier: string; count: number; amountCents: number }[];
}

/** `NONE` es la venta de contado; la pantalla la llama `CASH`. */
function toFinancier(silo: string): Financier {
    return silo === 'NONE' ? 'CASH' : (silo as Financier);
}

function toMix(silo: SiloSalesKpis['mix']): SalesMixSlice[] {
    return silo.map((slice) => ({
        financier: toFinancier(slice.financier),
        salesCount: slice.count,
        amountCents: slice.amountCents,
    }));
}

/** Una venta cancelada de `GET /backoffice/cancellations`; solo se leen los campos del Home. */
type SiloCancellation = Partial<Omit<CancellationItem, 'saleId' | 'amountCents'>> &
    Pick<CancellationItem, 'saleId' | 'amountCents'>;

interface SiloCancellations {
    count: number;
    cancellations: SiloCancellation[];
}

/**
 * Variación del ticket promedio contra el de la misma fecha hace 7 días, como fracción.
 * `null` sin piezas ni monto en la línea base: sin base no hay comparación.
 */
export function ticketChangeVsBaseline(
    averageTicketCents: number,
    baselineCents: number | null | undefined,
    baselineUnits: number | null | undefined,
): number | null {
    if (!baselineUnits || baselineUnits <= 0 || !baselineCents || baselineCents <= 0) return null;
    const baselineTicket = baselineCents / baselineUnits;
    return (averageTicketCents - baselineTicket) / baselineTicket;
}

const CANCELLATIONS_ENDPOINT = 'GET /backoffice/cancellations';

/**
 * Suma el monto de las cancelaciones y deja la lista; el conteo viene del silo.
 * Una respuesta vacía o sin lista no es «cero cancelaciones»: lanza `UnexpectedShapeError`.
 */
export function summarizeCancellations(silo: Partial<SiloCancellations> | null): Cancellations7d {
    if (!silo || !Array.isArray(silo.cancellations)) {
        throw new UnexpectedShapeError(CANCELLATIONS_ENDPOINT, 'falta la lista de cancelaciones');
    }
    const list = silo.cancellations;
    return {
        count: silo.count ?? list.length,
        amountCents: list.reduce((sum, item) => sum + item.amountCents, 0),
        items: list.map((item) => ({
            saleId: item.saleId,
            cancelledAt: item.cancelledAt ?? null,
            storeId: item.storeId ?? null,
            storeName: item.storeName ?? null,
            sellerId: item.sellerId ?? null,
            sellerName: item.sellerName ?? null,
            amountCents: item.amountCents,
        })),
    };
}

/** Traduce el silo al contrato; tiendas y vendedores activos no vienen y quedan en `null`. */
function adaptSalesKpis(silo: SiloSalesKpis): SalesKpisResponse {
    const mix = toMix(silo.mix);
    const surekeep = mix.find((slice) => slice.financier === 'SUREKEEP');

    return {
        soldTodayCents: silo.soldTodayCents,
        unitsToday: silo.unitsToday,
        averageTicketCents: silo.averageTicketCents,
        ticketChangeVs7d: ticketChangeVsBaseline(
            silo.averageTicketCents,
            silo.baselineSameWeekdayCents,
            silo.baselineSameWeekdayUnits,
        ),
        surekeepUnitsToday: surekeep?.salesCount ?? 0,
        mix,
        activeStoreCount: null,
        activeSellerCount: null,
        baselineSameDayLastWeekCents: silo.baselineSameWeekdayCents,
        baselineSameDayLastWeekUnits: silo.baselineSameWeekdayUnits ?? null,
    };
}

export const homeClient: HomeApi = {
    getSalesKpis: async () => {
        const silo = await apiFetch<SiloSalesKpis>('/backoffice/sales/kpis', { baseUrl: BASE_URL });
        return adaptSalesKpis(silo);
    },

    getCancellations7d: async () => {
        const silo = await apiFetch<SiloCancellations | null>('/backoffice/cancellations?days=7', {
            baseUrl: BASE_URL,
        });
        return summarizeCancellations(silo);
    },
};
